/**
 * "Help us improve" - the one place a person can say what Timo should do next.
 *
 * Deliberately three taps deep at most: pick what kind of thing it is, type it, send. No title
 * field, no category tree, no account to create. A feedback form that asks for six things is a
 * feedback form nobody fills in, and the details that actually matter - who, where, which build -
 * are ones the app already knows and can attach itself.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import { byId, esc, on, toast } from "../lib/dom";
import { currentPage } from "./shell";

const submit = httpsCallable<{ kind: string; message: string; page: string; platform: string; appVersion: string; userAgent: string }, { ok: boolean }>(functions, "submitFeedback");

const KINDS: [string, string, string][] = [
  ["idea", "An idea", "Something Timo should do"],
  ["problem", "A problem", "Something that went wrong"],
  ["other", "Something else", "Anything at all"],
];

export function openFeedback(kind: "idea" | "problem" | "other" = "idea") {
  document.getElementById("feedbackLayer")?.remove();
  const layer = document.createElement("div");
  layer.id = "feedbackLayer";
  layer.className = "modal-layer show";
  layer.innerHTML = `
    <div class="modal feedback-modal" role="dialog" aria-modal="true" aria-labelledby="fbTitle">
      <h2 id="fbTitle">Help us improve Timo</h2>
      <p class="meta">Tell us what would make this better. It goes straight to the people building it.</p>
      <div class="fb-kinds" role="radiogroup" aria-label="What kind of feedback">
        ${KINDS.map(([k, label, hint]) => `<button type="button" class="fb-kind${k === kind ? " active" : ""}" data-kind="${k}" role="radio" aria-checked="${k === kind}"><b>${label}</b><small>${hint}</small></button>`).join("")}
      </div>
      <textarea id="fbMessage" rows="5" maxlength="4000" placeholder="What happened, or what would you like Timo to do?"></textarea>
      <p class="meta fb-note">Timo attaches your email and which screen you were on, so we can follow up and reproduce it.</p>
      <div class="modal-actions"><span class="spacer"></span><button type="button" class="btn" id="fbCancel">Cancel</button><button type="button" class="btn primary" id="fbSend">Send</button></div>
    </div>`;
  document.body.appendChild(layer);

  let chosen = kind;
  const close = () => layer.remove();
  layer.querySelectorAll<HTMLElement>("[data-kind]").forEach((b) => on(b, "click", () => {
    chosen = b.dataset.kind as typeof chosen;
    layer.querySelectorAll<HTMLElement>("[data-kind]").forEach((x) => {
      const on_ = x === b;
      x.classList.toggle("active", on_);
      x.setAttribute("aria-checked", String(on_));
    });
  }));
  on(layer, "click", (e) => { if (e.target === layer) close(); });
  on(byId("fbCancel"), "click", close);
  // Escape closes it, and the listener goes when the sheet does rather than outliving it.
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey); } };
  document.addEventListener("keydown", onKey);

  const box = byId<HTMLTextAreaElement>("fbMessage")!;
  box.focus();
  on(byId("fbSend"), "click", async () => {
    const message = box.value.trim();
    if (message.length < 4) { box.focus(); toast("Tell us a little more than that"); return; }
    const btn = byId<HTMLButtonElement>("fbSend")!;
    btn.disabled = true; btn.textContent = "Sending…";
    try {
      await submit({ kind: chosen, message, page: currentPage(), platform: "web", appVersion: "", userAgent: navigator.userAgent });
      close();
      toast("Thank you - that reached us");
    } catch (err) {
      btn.disabled = false; btn.textContent = "Send";
      toast((err as Error).message);
    }
  });
}
